<template>
    <div>
      <Toast />
      <Toolbar class="mb-4">
        <template #start>
          <h1 class="text-2xl font-bold text-800">Gestión de Estados</h1>
        </template>
        <template #end>
          <Button label="Nuevo" icon="pi pi-plus" class="p-button-success mr-2" @click="openNew" />
        </template>
      </Toolbar>
  
      <DataTable 
        :value="estados" 
        :loading="loading" 
        scrollable="scroll"
        scrollHeight="400px"
        v-model:filters="filters"
        :globalFilterFields="['Nombre']"
      >
        <template #header>
          <div class="table-header">
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText
                type="text"
                v-model="filters['global'].value"
                placeholder="Buscar..."
              />
            </span>
          </div>
        </template>
        <Column field="Nombre" header="Nombre" :sortable="true"></Column>
        <Column header="Acciones" headerStyle="width: 120px; text-align: center" bodyStyle="text-align: center">
          <template #body="slotProps">
            <div class="flex justify-content-center gap-1">
              <Button 
                icon="pi pi-pencil" 
                class="p-button-rounded p-button-success"
                v-tooltip.top="'Editar'"
                @click="editEstado(slotProps.data)" 
              />
              <Button 
                icon="pi pi-trash" 
                class="p-button-rounded p-button-danger" 
                v-tooltip.top="'Eliminar'"
                @click="confirmDeleteEstado(slotProps.data)" 
              />
            </div>
          </template>
        </Column>
      </DataTable>
  
      <Dialog v-model:visible="estadoDialog" :style="{width: '450px'}" header="Detalles del Estado" :modal="true" class="p-fluid">
        <div class="field">
          <label for="nombre">Nombre</label>
          <InputText 
            id="nombre" 
            v-model.trim="estado.Nombre" 
            required="true" 
            autofocus 
            :class="{'p-invalid': submitted && !estado.Nombre}" 
          />
          <small class="p-error" v-if="submitted && !estado.Nombre">El nombre es requerido.</small>
        </div>
        <template #footer>
          <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="hideDialog" />
          <Button label="Guardar" icon="pi pi-check" class="p-button-text" @click="saveEstado" />
        </template>
      </Dialog>
  
      <Dialog v-model:visible="deleteEstadoDialog" :style="{width: '450px'}" header="Confirmar" :modal="true">
        <div class="confirmation-content">
          <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
          <span v-if="estado">¿Estás seguro de que quieres eliminar <b>{{estado.Nombre}}</b>?</span>
        </div>
        <template #footer>
          <Button label="No" icon="pi pi-times" class="p-button-text" @click="deleteEstadoDialog = false" />
          <Button label="Sí" icon="pi pi-check" class="p-button-text" @click="deleteEstado" />
        </template>
      </Dialog>
    </div>
  </template>
  
<script>
  import { ref, onMounted } from 'vue'
  import { useToast } from 'primevue/usetoast'
  import { FilterMatchMode } from "primevue/api";
  import EstadosService from '../../service/EstadosService'
  
  export default {
    setup() {
      const toast = useToast()
      const estadosService = new EstadosService()
      const estados = ref([])
      const estadoDialog = ref(false)
      const deleteEstadoDialog = ref(false)
      const estado = ref({})
      const submitted = ref(false)
      const loading = ref(true)
      const filters = ref({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
      });
  
      onMounted(() => {
        loadEstados()
      })
  
      const loadEstados = async () => {
        try {
          loading.value = true
          const data = await estadosService.getEstados(this)
          estados.value = data
        } catch (error) {
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los estados',
            life: 3000
          })
        } finally {
          loading.value = false
        }
      }
  
      const openNew = () => {
        estado.value = {}
        submitted.value = false
        estadoDialog.value = true
      }
  
      const hideDialog = () => {
        estadoDialog.value = false
        submitted.value = false
      }
  
      const editEstado = async (est) => {
        try {
          loading.value = true
          const data = await estadosService.getEstado(this, est.Id_estado)
          estado.value = {...data}
          estadoDialog.value = true
        } catch (error) {
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar el estado',
            life: 3000
          })
        } finally {
          loading.value = false
        }
      }
  
      const saveEstado = async () => {
        submitted.value = true
        
        if (estado.value.Nombre && estado.value.Nombre.trim()) {
          try {
            loading.value = true
            
            if (estado.value.Id_estado) {
              // Actualización
              await estadosService.updateEstado(this, estado.value)
              toast.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Estado actualizado',
                life: 3000
              })
            } else {
              // Creación
              await estadosService.addEstado(this, estado.value)
              toast.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Estado creado',
                life: 3000
              })
            }
            
            estadoDialog.value = false
            estado.value = {}
            await loadEstados()
          } catch (error) {
            toast.add({
              severity: 'error',
              summary: 'Error',
              detail: error.response?.data?.message || 'Error al guardar el estado',
              life: 3000
            })
          } finally {
            loading.value = false
          }
        }
      }
  
      const confirmDeleteEstado = (est) => {
        estado.value = est
        deleteEstadoDialog.value = true
      }
  
      const deleteEstado = async () => {
        try {
          loading.value = true
          // Asumiendo que tienes el ID de usuario en el store
        //   const id_usuario = this.$store.getters.getUsuario?.Id_usuario || 0
          await estadosService.deleteEstado(this, estado.value.Id_estado)
          toast.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Estado eliminado',
            life: 3000
          })
          deleteEstadoDialog.value = false
          estado.value = {}
          await loadEstados()
        } catch (error) {
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.message || 'Error al eliminar el estado',
            life: 3000
          })
        } finally {
          loading.value = false
        }
      }
  
      return {
        estados,
        estadoDialog,
        deleteEstadoDialog,
        estado,
        submitted,
        loading,
        openNew,
        hideDialog,
        editEstado,
        saveEstado,
        confirmDeleteEstado,
        deleteEstado,
        filters
      }
    }
  }
</script>
  
  <style scoped>
  /* Estilos personalizados si son necesarios */
  </style>